//#region imports
import React, { useState, useEffect } from 'react';

// bootstrap
import { Container, Row, Col, Card, Button, Alert } from 'react-bootstrap';

// firebase
import fire from '_utils/firebase';
import { sendEmailVerification } from 'firebase/auth';

// styling
import { Colors } from '_styles';

// components
import LogoutButton from '_components/atoms/button/logout-button';

//#endregion

const EmailVerification = () => {

  //#region local states
  const [loading, setLoading] = useState(false);                                          // indicates if an async operation is in progress
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'danger' });    // alert state
  const [isEmailSent, setIsEmailSent] = useState(false);                                  // tracks if verification email was sent
  //#endregion

  // firebase auth
  const currentUser = fire.auth().currentUser;

  // function to handle sending verification email
  const handleSendVerificationEmail = async () => {

    if (!currentUser) {
      setAlert({ show: true, message: 'No authenticated user found. Please log in first.', variant: 'danger' });
      return;
    }

    setLoading(true);

    try {
      await sendEmailVerification(currentUser);
      setIsEmailSent(true);
      setAlert({ show: true, message: `Verification email sent to ${currentUser.email}`, variant: 'success' });
    }
    catch (error) {
      console.error('Error sending verification email:', error);
      setAlert({ show: true, message: 'Failed to send verification email. Please try again.', variant: 'danger' });
    }
    finally {
      setLoading(false);
    }
  };

  // handle checking if email is verified
  const handleCheckVerification = async () => {

    if (!currentUser) {
      setAlert({ show: true, message: 'No authenticated user found.', variant: 'danger' });
      return;
    }

    setLoading(true);

    try {
      // reload user to get latest verification status
      await currentUser.reload();

      if (fire.auth().currentUser.emailVerified) {
        setAlert({ show: true, message: 'Email verified successfully! Continuing...', variant: 'success' });

        // refresh the token so backend checks see the verified email
        await fire.auth().currentUser.getIdToken(true);

        // reload to let the root router move to the next onboarding step
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setAlert({ show: true, message: 'Email not yet verified. Please check your email and click the verification link.', variant: 'warning' });
      }
    } catch (error) {
      console.error('Error checking verification:', error);
      setAlert({ show: true, message: 'Error checking verification status. Please try again.', variant: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  // function to handle logout error
  const handleLogoutError = () => {
    setAlert({ show: true, message: 'Error during logout. Please try again.', variant: 'danger' });
  };

  // show alert for 5 seconds
  useEffect(() => {
    if (alert.show) {
      const timer = setTimeout(() => {
        setAlert({ show: false, message: '', variant: 'danger' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alert.show]);

  return (
    <Container fluid className="d-flex align-items-center justify-content-center min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
      <Row className="w-100 justify-content-center">
        <Col xs={12} md={8} lg={6} xl={4}>
          <Card className="shadow-sm">
            <Card.Body className="p-5">

              {/* title */}
              <div className="text-center mb-4">
                <h2 style={{ color: Colors.PRM_LIGHT_BLUE, fontWeight: 'bold' }}>
                  Email Verification
                </h2>
                <p className="text-muted">
                  {!isEmailSent
                    ? 'Verify your email address to continue'
                    : 'Check your email and click the verification link'
                  }
                </p>
              </div>

              {alert.show && (
                <Alert variant={alert.variant} className="mb-3">
                  {alert.message}
                </Alert>
              )}

              {currentUser && (
                <div className="text-center mb-4">
                  <p className="text-muted">
                    Email: <strong>{currentUser.email}</strong>
                  </p>
                </div>
              )}

              {!isEmailSent ? (
                <Button
                  onClick={handleSendVerificationEmail}
                  className="w-100 mb-3"
                  style={{ backgroundColor: Colors.PRM_LIGHT_BLUE, borderColor: Colors.PRM_LIGHT_BLUE }}
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Verification Email'}
                </Button>
              ) : (
                <div>
                  <Button
                    onClick={handleCheckVerification}
                    className="w-100 mb-3"
                    style={{ backgroundColor: Colors.PRM_LIGHT_BLUE, borderColor: Colors.PRM_LIGHT_BLUE }}
                    disabled={loading}
                  >
                    {loading ? 'Checking...' : 'I\'ve Verified My Email'}
                  </Button>

                  <div className="text-center">
                    <Button
                      variant="link"
                      onClick={handleSendVerificationEmail}
                      disabled={loading}
                      className="p-0"
                    >
                      Didn't receive the email? Resend
                    </Button>
                  </div>
                </div>
              )}

              {/* Logout Button */}
              <div className="text-center mt-3">
                <LogoutButton
                  disabled={loading}
                  onLogoutError={handleLogoutError}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default EmailVerification;
